import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import {
  FormatListBulletedOutlined as ListIcon,
  NotificationsActiveOutlined as FollowUpIcon,
  ArchiveOutlined as ClosedIcon,
  PersonAddAlt1Outlined as AddIcon,
  ExpandMoreOutlined as ExpandIcon,
} from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { NavPanel, PanelGroup, PanelLink } from '../../components/layout/NavPanel';
import { usePipelineOrder } from './pipelineCache';
import { STAGE_COLOR, STAGE_LABEL, candidateName } from './hiringUI';
import useStalled, { refreshStalled } from './useStalled';
import AutoCloseNotice from './AutoCloseNotice';
import AddCandidateDialog from './AddCandidateDialog';
import ImportCandidatesDialog from './ImportCandidatesDialog';

// Every hiring screen shares this, so the panel does not change under you
// when you move between the board and a candidate.
//
// The create action lives here rather than above a table, per the shell's
// rule: the panel is where a section's views and its primary create action
// live.

// The count is the panel's own business, not something every page has to
// remember to pass in. When it was a prop, the candidate page rendered
// `<HiringPanel />` with nothing and the badge disappeared while you read
// somebody's record.
// The panel OWNS the create action and the dialogs behind it.
//
// They used to be props every page had to remember to pass, and Insights
// did not — so "Add candidate" simply vanished when you opened it. Same
// class of bug as the stalled badge disappearing on the candidate page:
// a thing that belongs to the section was being supplied by whichever
// screen happened to be on.
/** Whether the queue is open, across visits. */
const QUEUE_OPEN = 'toposds.hiring.queueOpen';

/** The same words the table uses, short enough for a 232px column. */
const touched = (days: number | null | undefined) => {
  if (days == null) return 'not touched';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};

/**
 * The other candidates, in the panel that is already there.
 *
 * 232px is enough for a name and a line saying where they are, which is
 * exactly what picking the next person needs. It scrolls on its own so the
 * section's links never leave the screen.
 */
const PipelineList: React.FC<{ currentId: string }> = ({ currentId }) => {
  const navigate = useNavigate();
  const { rows } = usePipelineOrder();
  const current = useRef<HTMLDivElement>(null);
  /**
   * Shut to begin with, and remembered after that. Somebody who never
   * switches candidates should not pay for the list with a panel full of
   * other people's names; somebody working down a queue opens it once and
   * finds it open on the next person.
   */
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(QUEUE_OPEN) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(QUEUE_OPEN, open ? '1' : '0'); } catch { /* private mode */ }
  }, [open]);

  // Keep the one you are reading in view, including when you arrive from
  // a link rather than from the table.
  useEffect(() => {
    if (!open) return;
    // Guarded: not every environment implements it (jsdom does not), and
    // failing to scroll must never take the panel down with it.
    current.current?.scrollIntoView?.({ block: 'nearest' });
  }, [currentId, rows.length, open]);

  if (rows.length === 0) return null;

  return (
    /* Set apart from the links above it on purpose: those are four views
       that never change, this is a list of people that changes all day.
       Its own ground, its own rule above it, denser type — so the panel
       reads as "the section, then the queue" rather than as one long list
       where a person sits next to "Insights".

       It is also the ONLY part of the panel that scrolls. The section's
       links and the Add button are what somebody reaches for without
       looking; scrolling a list of twenty-six people used to take them
       off the screen. */
    <Box
      sx={{
        mt: 1, mx: -0.5, pt: 1.5, px: 0.5,
        borderTop: '1px solid', borderColor: 'divider',
        display: 'flex', flexDirection: 'column',
        // Takes the space that is left when open, takes none when shut.
        flex: open ? '1 1 auto' : '0 0 auto',
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); }
        }}
        sx={{
          px: 1.25, py: 0.25, mb: open ? 0.75 : 0, borderRadius: 1, cursor: 'pointer', flexShrink: 0,
          '&:hover': { bgcolor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        <Typography variant="overline" sx={{ color: 'text.disabled', flex: 1, minWidth: 0 }}>
          Candidates · {rows.length}
        </Typography>
        <ExpandIcon
          fontSize="small"
          sx={{
            color: 'text.disabled',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
      </Stack>

      {/* Runs the whole way down, and scrolls on its own. It used to be
          capped at 360px inside a column that scrolled as a whole — two
          bars, the list cut off mid-name, and the Add button disappearing
          when you went looking for somebody. */}
      {open && (
        <Stack gap={0.25} sx={{ pb: 1, overflowY: 'auto', minHeight: 0 }}>
          {rows.map((row) => {
            const active = row._id === currentId;
            return (
              <Box
                key={row._id}
                ref={active ? current : undefined}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/hiring/${row._id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/hiring/${row._id}`); }
                }}
                sx={{
                  px: 1.25, py: 0.6, borderRadius: 1, cursor: 'pointer', minWidth: 0, flexShrink: 0,
                  bgcolor: active ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
                  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                }}
              >
                {/* Name first, number after: you look for a person by name,
                    and the number is how you say which one you mean. */}
                <Stack direction="row" alignItems="baseline" gap={0.5} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2" noWrap
                    // The name is what the row IS; the rest is annotation.
                    sx={{ fontWeight: active ? 700 : 600, fontSize: 13.5, minWidth: 0 }}
                  >
                    {candidateName(row)}
                  </Typography>
                  {row.num != null && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10.5, flexShrink: 0 }}>
                      · {row.num}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    bgcolor: STAGE_COLOR[row.stage] || 'transparent',
                  }} />
                  <Typography variant="caption" color="text.disabled" noWrap sx={{ fontSize: 10.5 }}>
                    {STAGE_LABEL[row.stage] || row.stage} · {touched(row.daysSinceActivity)}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

/** Everything under /hiring that is a screen rather than a person. */
const VIEWS = ['list', 'follow-up', 'closed', 'insights'];

// Which list a candidate page belongs to: the one you opened them from.
// "To do" also lives at /hiring/follow-up, which its exact-match link never
// lit, and a candidate page lit nothing at all.
type HiringView = 'list' | 'todo' | 'closed' | 'insights';
let lastList: HiringView = 'list';

export function hiringSelection(pathname: string): HiringView {
  const path = pathname.replace(/\/+$/, '');
  if (path === '/hiring' || path === '/hiring/follow-up') return (lastList = 'todo');
  if (path === '/hiring/list') return (lastList = 'list');
  if (path === '/hiring/closed') return (lastList = 'closed');
  if (path === '/hiring/insights') return 'insights';
  return lastList;
}

const HiringPanel: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const { pathname } = useLocation();
  // A candidate page, as opposed to a view. Named by the VIEWS rather
  // than by the shape of an id: matching /[0-9a-f]{24}/ looked tighter and
  // meant the panel silently did nothing anywhere ids are not Mongo's.
  const match = pathname.match(/^\/hiring\/([^/]+)$/);
  const openCandidateId = match && !VIEWS.includes(match[1]) ? match[1] : null;
  const selected = hiringSelection(pathname);
  const navigate = useNavigate();
  const { items: stalled } = useStalled();
  const stalledCount = stalled.length;
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  return (
    <NavPanel>
      {/* At the TOP, like Users. It was at the foot of the panel here and
          above the table on Fleet, so the button somebody reaches for most
          moved depending on which tab they were on. Importing lives inside
          the dialog now — it is another way to add people, not a second
          thing to decide between before you have decided to add anybody. */}
      <Button
        fullWidth
        variant="contained"
        disableElevation
        startIcon={<AddIcon />}
        onClick={() => setAddOpen(true)}
        sx={{ mb: 2.5, py: 1 }}
      >
        Add candidate
      </Button>

      <PanelGroup label="Pipeline">
        <PanelLink to="/hiring/list" icon={<ListIcon />} label="All candidates" active={selected === 'list'} currentPath={pathname} />
        <PanelLink
          to="/hiring"
          active={selected === 'todo'}
          icon={<FollowUpIcon />}
          label="To do"
          // A badge is a demand and only shows when it is not zero — the
          // rule the vault rail set. A quiet pipeline should look quiet.
          badge={stalledCount || undefined}
          currentPath={pathname}
        />
        <PanelLink to="/hiring/closed" icon={<ClosedIcon />} label="Closed" active={selected === 'closed'} currentPath={pathname} />
      </PanelGroup>

      {/* On a candidate, the rest of the pipeline. Working a list of
          people one after another is what this screen is for, and going
          back to the table between each of them was the whole cost. The
          order is the order the table showed — the same rows, so a filter
          or a sort you set there still governs who comes next. */}
      {openCandidateId && <PipelineList currentId={openCandidateId} />}

      <AddCandidateDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onImport={() => { setAddOpen(false); setImportOpen(true); }}
        onCreated={(candidate) => {
          setAddOpen(false);
          refreshStalled(true);
          navigate(`/hiring/${candidate._id}`);
        }}
      />

      {/* Mounted with the rail, so it is seen once per visit to hiring
          rather than once per screen inside it. */}
      <AutoCloseNotice />

      <ImportCandidatesDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { refreshStalled(true); onChanged?.(); }}
      />
    </NavPanel>
  );
};

export default HiringPanel;
